import {fabric} from 'fabric';
import {PIXEL_STUDIO_CUSTOM_PROPS} from './constants';
import {createTextbox} from '../editor/objects/textbox';
import {getSystemClipboard, uuid} from './index';
import {createFImage} from '../editor/objects/image';
import {handleMouseOutCorner} from '../editor/controller';
import Editor from '../editor';
import {message} from 'antd';

// @ts-ignore fabric controlsUtils
const {controlsUtils} = fabric;

export const requestRender = (ob: any) => {};

export const calcCanvasZoomLevel = (
    containerSize: {width: any; height: any},
    sketchSize: {width: any; height: any},
) => {
    if (sketchSize.width < containerSize.width && sketchSize.height <= containerSize.height) {
        return 1;
    }

    let level = 1;
    if (containerSize.width / containerSize.height < sketchSize.width / sketchSize.height) {
        level = containerSize.width / sketchSize.width;
    } else {
        level = containerSize.height / sketchSize.height;
    }

    level = Number(level.toFixed(2));
    return level;
};

let _clipboard: {clone: (arg0: (cloned: any) => void, arg1: string[]) => void};

export const copyObject = async (canvas: fabric.Canvas, target: fabric.Object | null) => {
    // clone what are you copying since you
    // may want copy and paste on different moment.
    // and you do not want the changes happened
    // later to reflect on the copy.
    return new Promise((resolve) => {
        if (!target) {
            target = canvas.getActiveObject();
        }
        if (!target) return Promise.resolve(false);

        // Empty clipboard
        navigator.clipboard.writeText('');
        return target.clone((cloned: {clone: (arg0: (cloned: any) => void, arg1: string[]) => void}) => {
            _clipboard = cloned;
            return resolve(true);
        }, PIXEL_STUDIO_CUSTOM_PROPS);
    });
};

export const pasteObject = async (canvas: fabric.Canvas) => {
    // Try reading the system clipboard first
    try {
        // @ts-ignore
        const {type, result} = (await getSystemClipboard()) || {};
        if (result) {
            if (type === 'text') {
                createTextbox({text: result, canvas});
            } else if (type === 'image') {
                createFImage({imageSource: result, canvas});
            }
            return;
        }
    } catch (err) {
        console.error('Failed to read clipboard contents: ', err);
    }

    // clone again, so you can do multiple copies.
    _clipboard.clone((cloned) => {
        canvas.discardActiveObject();
        cloned.set({
            left: cloned.left + 50,
            top: cloned.top + 50,
            evented: true,
        });

        if (cloned.type === 'f-line' || cloned.type === 'f-arrow' || cloned.type === 'f-tri-arrow') {
            handleFLinePointsWhenMoving({
                target: cloned,
                transform: {original: {left: cloned.left - 50, top: cloned.top - 50}},
            });
        }

        if (cloned.type === 'activeSelection') {
            // active selection needs a reference to the canvas.
            cloned.canvas = canvas;
            cloned.forEachObject((obj: fabric.Object) => {
                canvas.add(obj);
            });
            // this should solve the unselectability
            cloned.setCoords();
        } else {
            canvas.add(cloned);
        }

        canvas.setActiveObject(cloned);
        canvas.requestRenderAll();
        canvas.fire('pixel_studio:clone', {target: cloned});
    }, PIXEL_STUDIO_CUSTOM_PROPS);
};

export const removeObject = (target: any, canvas: fabric.Canvas) => {
    if (!target) {
        target = canvas.getActiveObject();
    }
    if (!target) return;
    if (target.type === 'activeSelection') {
        target.getObjects().forEach((obj: fabric.Object) => {
            canvas.remove(obj);
        });
        canvas.discardActiveObject();
    } else {
        canvas.remove(target);
    }
    handleMouseOutCorner(target);
    canvas.requestRenderAll();
    // canvas.fire('pixel_studio:del', {target: null});
    return true;
};

export const groupSelection = (canvas: fabric.Canvas, target: any) => {
    if (!target) {
        target = canvas.getActiveObject();
    }
    if (!target || target.type !== 'activeSelection') {
        return;
    }
    canvas.getActiveObjects().forEach((o) => {
        if (o.type === 'f-image') {
            // @ts-ignore
            o.img.clipPath = null;
        }
    });
    target.toGroup();
    canvas.requestRenderAll();
    canvas.fire('pixel_studio:group');
};

export const ungroup = (canvas: fabric.Canvas, target: any) => {
    if (!target) {
        target = canvas.getActiveObject();
    }
    if (!target || target.type !== 'group') {
        return;
    }
    target.getObjects().forEach((obj: any) => {
        obj.set({
            lockMovementX: false,
            lockMovementY: false,
            hasControls: true,
            selectable: true,
        });
    });
    target.toActiveSelection();
    canvas.requestRenderAll();
    canvas.fire('pixel_studio:ungroup');
};

export const changeLayerLevel = (level: any, editor: any, target: any) => {
    if (!target) {
        target = editor.canvas.getActiveObject();
    }
    if (!target || target.type === 'activeSelection') {
        return;
    }
    switch (level) {
        case 'layer-up':
            target.bringForward();
            break;
        case 'layer-top':
            target.bringToFront();
            break;
        case 'layer-down':
            target.sendBackwards();
            break;
        case 'layer-bottom':
            target.sendToBack();
            break;
        default:
            break;
    }
    editor.sketch.sendToBack();
    editor.canvas.requestRenderAll();
    editor.fireCustomModifiedEvent();
};

/**
 * Transforms a point described by x and y in a distance from the top left corner of the object
 * bounding box.
 * @param {Object} transform
 * @param {String} originX
 * @param {String} originY
 * @param {number} x
 * @param {number} y
 * @return {Fabric.Point} the normalized point
 */
export const getLocalPoint = (transform: any, originX: any, originY: any, x: number, y: number) => {
    var target = transform.target,
        control = target.controls[transform.corner],
        zoom = target.canvas.getZoom(),
        padding = target.padding / zoom,
        localPoint = target.toLocalPoint(new fabric.Point(x, y), originX, originY);
    if (localPoint.x >= padding) {
        localPoint.x -= padding;
    }
    if (localPoint.x <= -padding) {
        localPoint.x += padding;
    }
    if (localPoint.y >= padding) {
        localPoint.y -= padding;
    }
    if (localPoint.y <= padding) {
        localPoint.y += padding;
    }
    localPoint.x -= control.offsetX;
    localPoint.y -= control.offsetY;
    return localPoint;
};

function isTransformCentered(transform: {target?: any; originX?: any; originY?: any; corner?: string | number}) {
    return transform.originX === 'center' && transform.originY === 'center';
}

const _changeHeight = (
    eventData: any,
    transform: {
        target?: any;
        originX?: any;
        originY?: any;
        corner?: string | number;
    },
    x: number,
    y: number,
) => {
    const target = transform.target,
        localPoint = getLocalPoint(transform, transform.originX, transform.originY, x, y),
        strokePadding = target.strokeWidth / (target.strokeUniform ? target.scaleX : 1),
        multiplier = isTransformCentered(transform) ? 2 : 1,
        oldHeight = target.height,
        newHeight = Math.abs((localPoint.y * multiplier) / target.scaleY) - strokePadding;
    target.set('height', Math.max(newHeight, 0));
    return oldHeight !== newHeight;
};

export const changeHeight = controlsUtils.wrapWithFireEvent(
    'resizing',
    controlsUtils.wrapWithFixedAnchor(_changeHeight),
);

export const handleFLinePointsWhenMoving = (opt: any) => {
    const {target, transform, action} = opt;
    if (action === 'line-points-change') return;
    const {original} = transform;
    const deltaLeft = target.left - original.left;
    const deltaTop = target.top - original.top;
    target.set({
        x1: target.x1 + deltaLeft,
        y1: target.y1 + deltaTop,
        x2: target.x2 + deltaLeft,
        y2: target.y2 + deltaTop,
    });
};

export const replaceImage = async (
    currentObject: fabric.Object,
    modifiedUrl: string,
    editor: Editor | undefined,
    originalSource?: string,
    isCdnImage?: boolean,
) => {
    const {left, top, scaleX, scaleY, angle, width, height} = currentObject;

    // Load the new image
    const imgElement = new Image();
    imgElement.crossOrigin = 'Anonymous';
    imgElement.src = modifiedUrl;

    imgElement.onload = () => {
        const newImg = new fabric.Image(imgElement);
        const newWidth = newImg.width || width;
        const newHeight = newImg.height || height;
        // @ts-ignore
        const newScaleX = (width * scaleX) / newWidth;
        // @ts-ignore
        const newScaleY = (height * scaleY) / newHeight;

        newImg.set({
            left,
            top,
            scaleX: newScaleX,
            scaleY: newScaleY,
            angle,
            width: newWidth,
            height: newHeight,
        });

        // @ts-ignore
        const fimg = new fabric.FImage({
            image: newImg,
            id: uuid(),
            originalSource: originalSource,
            isCdnImage: isCdnImage,
            isBgRemoved: true,
        });
        if (editor) {
            editor.canvas.remove(currentObject);
            editor.canvas.viewportCenterObject(fimg);
            editor.canvas.add(fimg);
            editor.canvas.setActiveObject(fimg);
            editor.canvas.remove(currentObject);
            editor.canvas.renderAll();
            return 'Success';
        }
    };

    imgElement.onerror = () => {
        message.error('Something went wrong while background operation. Please retry.');
        console.error('Failed to load the modified image.');
        return 'Failure';
    };
};

export const fetchImageAsBlob = async (url: string): Promise<Blob> => {
    const response = await fetch(url);
    return await response.blob();
};

const typeMap: {[key: string]: string} = {
    'f-image': 'Image',
    path: 'Shape',
    'f-text': 'Text',
};

export const TypeMapper = (type: string) => {
    if (typeMap[type]) {
        return typeMap[type];
    }
    return 'Element';
};

export const getDayCounter = (dateJoined: string) => {
    const joinedDate = new Date(dateJoined);
    const today = new Date();
    const threshHoldDate = new Date('2024-07-19T00:00');
    if (joinedDate < threshHoldDate) {
        return 0;
    }

    const joinedUTC = Date.UTC(joinedDate.getUTCFullYear(), joinedDate.getUTCMonth(), joinedDate.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

    const diffInMilliseconds = todayUTC - joinedUTC;
    const dayNumber = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24)) + 1;

    if (dayNumber <= 7) {
        return dayNumber;
    } else {
        return -1;
    }
};

export const getDaySinceJoined = (dateJoined: string) => {
    const joinedDate = new Date(dateJoined);
    const today = new Date();

    const joinedUTC = Date.UTC(joinedDate.getUTCFullYear(), joinedDate.getUTCMonth(), joinedDate.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

    const diffInMilliseconds = todayUTC - joinedUTC;
    const dayNumber = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24)) + 1;

    return dayNumber;
};
